// Session migration utilities
// Automatically migrates sessions to current version

import {
  SESSION_VERSION,
  TOOL_SCHEMA_VERSION,
  TOOL_ARCHITECTURE,
  needsMigration,
} from './version.js'
import {
  sanitizeMessages,
  addMigrationNotice,
  estimateCleanup,
} from './sanitizer.js'

/**
 * Migrate a session to current version
 *
 * @param {Object} session - Session data with metadata and messages
 * @param {Object} options - Migration options
 * @returns {Object} Migrated session
 */
export const migrateSession = async (session, options = {}) => {
  const { addNotice = true, verbose = false } = options

  // Determine old version (default to 1.0 if not specified)
  const oldVersion = session.metadata?.version || '1.0'

  // Check if migration is actually needed
  if (!needsMigration(session.metadata)) {
    if (verbose) {
      console.log(`[Migration] Session already at version ${SESSION_VERSION}`)
    }
    return session
  }

  console.log(
    `[Migration] Migrating session from ${oldVersion} to ${SESSION_VERSION}`
  )

  // Estimate cleanup
  const estimate = estimateCleanup(session.messages || [])
  console.log(
    `[Migration] Will keep ${estimate.willKeep}/${estimate.total} messages`
  )
  console.log(
    `[Migration] Removing ${estimate.toolCallMessages} tool call messages, ${estimate.toolResponseMessages} tool response messages`
  )

  // Sanitize messages
  let migratedMessages = sanitizeMessages(session.messages || [])

  // Add migration notice if requested
  if (addNotice && migratedMessages.length > 0) {
    migratedMessages = addMigrationNotice(
      migratedMessages,
      oldVersion,
      SESSION_VERSION
    )
  }

  // Update metadata
  const migratedMetadata = {
    ...session.metadata,
    version: SESSION_VERSION,
    schemaVersion: TOOL_SCHEMA_VERSION,
    toolArchitecture: TOOL_ARCHITECTURE,
    migrationHistory: [
      ...(session.metadata?.migrationHistory || []),
      {
        from: oldVersion,
        to: SESSION_VERSION,
        date: new Date().toISOString(),
        reason: 'Tool architecture upgrade',
        messagesRemoved: estimate.willRemove,
        messagesKept: estimate.willKeep,
      },
    ],
  }

  console.log(`[Migration] Migration complete: ${oldVersion} → ${SESSION_VERSION}`)

  return {
    metadata: migratedMetadata,
    messages: migratedMessages,
  }
}

/**
 * Check if session needs migration and migrate if needed
 * This is the main entry point for auto-migration
 *
 * @param {Object} session - Session data
 * @param {Object} options - Migration options
 * @returns {Object} Migrated session (or original if no migration needed)
 */
export const autoMigrate = async (session, options = {}) => {
  if (!session || !session.metadata) {
    console.warn('[AutoMigrate] Invalid session data, skipping migration')
    return session
  }

  if (needsMigration(session.metadata)) {
    console.log(
      '[AutoMigrate] Detected incompatible session version, migrating...'
    )
    return await migrateSession(session, options)
  }

  return session
}

/**
 * Validate migrated session
 * Ensures migration produced valid results
 *
 * @param {Object} migratedSession - Migrated session
 * @returns {boolean} True if valid
 */
export const validateMigratedSession = (migratedSession) => {
  if (!migratedSession || !migratedSession.metadata) {
    console.error('[Validation] Missing metadata')
    return false
  }

  if (migratedSession.metadata.version !== SESSION_VERSION) {
    console.error('[Validation] Version mismatch after migration')
    return false
  }

  if (!Array.isArray(migratedSession.messages)) {
    console.error('[Validation] Messages is not an array')
    return false
  }

  // Check for lingering tool calls (should be removed)
  const hasToolCalls = migratedSession.messages.some(
    msg => msg.role === 'assistant' && msg.toolCalls
  )
  if (hasToolCalls) {
    console.warn('[Validation] Found tool calls after migration (may be expected)')
  }

  // Check for tool response messages (should be removed)
  const hasToolResponses = migratedSession.messages.some(
    msg => msg.role === 'tool'
  )
  if (hasToolResponses) {
    console.error('[Validation] Found tool responses after migration')
    return false
  }

  return true
}

/**
 * Get migration summary for logging
 *
 * @param {Object} session - Original session
 * @param {Object} migratedSession - Migrated session
 * @returns {Object} Migration summary
 */
export const getMigrationSummary = (session, migratedSession) => {
  const oldVersion = session.metadata?.version || '1.0'
  const newVersion = migratedSession.metadata.version

  return {
    fromVersion: oldVersion,
    toVersion: newVersion,
    originalMessageCount: session.messages?.length || 0,
    migratedMessageCount: migratedSession.messages?.length || 0,
    messagesRemoved:
      (session.messages?.length || 0) - (migratedSession.messages?.length || 0),
    migrationHistory: migratedSession.metadata.migrationHistory,
    timestamp: new Date().toISOString(),
  }
}
